import api from "@/api";
import { defineStore } from "pinia";

import { School } from "@/interfaces/school.interface";
import { SchoolGroup } from "@/interfaces/school-group.interface";
import i18n from "@/plugins/i18n";
import { Admin, NewAdminDetails } from "@/interfaces/admin.interface";

export const useSchoolGroupStore = defineStore({
  id: "schoolGroupStore",
  state: () => ({
    loading: false,
    schoolGroupLoader: false,
    schoolGroupByIdLoader: false,
    schools: [] as School[],
    schoolGroups: [] as SchoolGroup[],
    admins: [] as Admin[],
    activeSchoolGroup: null as SchoolGroup | null,
    currentSchool: null as School | null,
    loadingMessage: "",
    loader: {
      creatingAdmin: false,
      schoolGroupUpdate: false,
    },
    champions: [],
  }),
  getters: {
    getCurrentSchoolGroup(): SchoolGroup | null {
      return this.activeSchoolGroup;
    },
    getSchoolGroups(): SchoolGroup[] {
      return this.schoolGroups;
    },
    getSchools(): School[] {
      return this.schools;
    },
    isLoading(): boolean {
      return this.loading;
    },
  },
  actions: {
    addSchoolGroupChampion(admin: Admin) {
      this.activeSchoolGroup?.schoolGroupChampions.push(admin);
    },
    addSchoolGroup(schoolGroup: SchoolGroup) {
      this.schoolGroups.push(schoolGroup);
    },
    async updateSchoolGroup(schoolGroupId: any, updateData: any) {
      this.loader.schoolGroupUpdate = true;
      try {
        const response = await api.put(
          `/v2/organisation/school-group/update/${schoolGroupId}`,
          updateData
        );
    
        if (response.status === 200) {
          const updatedSchoolGroup = response.data.data;
    
          // Update the active school group
          this.activeSchoolGroup = updatedSchoolGroup;
    
          // Find the index of the updated school group in the array
          const index = this.schoolGroups.findIndex(
            (group) => group._id === schoolGroupId
          );
    
          if (index !== -1) {
            // Replace the old object with the new updated object
            this.schoolGroups[index] = updatedSchoolGroup;
          } else {
            // If it doesn't exist, add it (fallback case)
            this.schoolGroups.push(updatedSchoolGroup);
          }
    
          console.log("School group updated successfully:", updatedSchoolGroup);
          return updatedSchoolGroup;
        } else {
          console.error("Failed to update school group");
          return false;
        }
      } catch (error) {
        console.error("Error while updating school group:", error);
        return false;
      } finally {
        this.loader.schoolGroupUpdate = false;
      }
    },        
    async fetchSchooGroupById(schoolGroupId: any) {
      this.schoolGroupByIdLoader = true;
      try {
        const response = await api.get(
          `/v2/organisation/school-group/structure/${schoolGroupId}`
        );
        if (response.status === 200) {
          this.activeSchoolGroup = response.data.data;
          return true;
        } else {
          console.error("Failed to fetch school group");
          return false;
        }
      } catch (error) {
        console.error("Error while fetching school group:", error);
        return false;
      } finally {
        this.schoolGroupByIdLoader = false;
      }
    },
    async createNewAdmin(adminDetails: NewAdminDetails) {
      try {
        this.loader.creatingAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.creatingAdmin"
        );

        const response = await api.post(
          "/v2/access/admin/initialise-new",
          adminDetails
        );
        if (response.data.success) {
          this.admins.push(response.data.data); 
          return response.data.data;
        } else {
          console.error("Failed to create admin:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error creating admin:", error);
        return false;
      } finally {
        this.loader.creatingAdmin = false;
        this.loadingMessage = "";
      }
    },
    async createSchoolGroup(schoolGroup: any) {
      this.schoolGroupLoader = true;
      try {
        const response = await api.post(
          `/v2/organisation/school-group/new`,
          schoolGroup
        );
        if (response.status === 201) {
          const newSchoolGroup = response.data.data;
          //this.schoolGroups.push(newSchoolGroup);
          return newSchoolGroup;
        } else {
          console.error("Failed to create school group");
          return false;
        }
      } catch (error) {
        console.error("Error while creating school group:", error);
        return false;
      } finally {
        this.schoolGroupLoader = false;
      }
    },
    setCurrentSchool(school: School) {
      this.currentSchool = school;
    },
    async fetchSchoolGroups() {
      this.loading = true;
      try {
        const response = await api.get(`v2/organisation/school-group/all`);
        if (response.status === 200) {
          this.schoolGroups = response.data.data;
        } else {
          console.error("Failed to fetch school groups");
        }
      } catch (error) {
        console.error("Error while fetching school groups:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchSchoolsBySchoolGroupId(schoolGroupId: any) {
      this.loading = true;
      try {
        const response = await api.get(
          `/v2/organisation/school-group/schools/${schoolGroupId}`
        );
        if (response.status === 200) {
          this.schools = response.data.data;
          return this.schools;
        } else {
          console.error("Failed to fetch school groups");
          return false;
        }
      } catch (error) {
        console.error("Error while fetching school groups:", error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async archiveOrUnarchiveSchoolGroup(schoolGroupId: string, action: "archive" | "unarchive") {
      this.loading = true;
      try {
        const response = await api.post(`/v2/organisation/school-group/archiving`, {
          schoolGroupId,
          action,
        });
        if (response.status === 200) {
          return response.data;
        } else {
          console.error(`Failed to ${action} school group`);
          return false;
        }
      } catch (error) {
        console.error(`Error while trying to ${action} school group:`, error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async fetchAllSchoolChampions(schoolGroupId: string) {
      this.loading = true;
      try {
        const response = await api.get(`/v2/organisation/school-group/${schoolGroupId}/school-champions`);
        if (response.status === 200) {
          this.champions = response.data.data;
          return this.champions;
        } else {
          console.error('Failed to fetch all school champions');
          return false;
        }
      } catch (error) {
        console.error('Error while fetching all school champions:', error);
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
  persist: true,
});
